// src/components/ui/GuidePanel.js
//
// Reusable shell for multi-page guide popovers.
// Handles page dots, title/body layout, and Close/Next/Got it footer.
// The parent component is responsible for:
//   - Positioning (left, top via `style`)
//   - Entry/exit animation (transform, opacity, transition via `style`)
//   - Any arrow element (passed as `children` — rendered inside the panel)
//
// Title rendering rules:
//   titlePrefix + non-empty title → "Prefix: Italic Title"
//   titlePrefix + empty title     → "Prefix" (bold, no colon)
//   no titlePrefix                → just italic title
//
// Per-step button labels:
//   step.nextLabel  overrides the default "Next" label
//   (last step always shows "Got it")
//
// Usage:
//   <GuidePanel
//     ref={guideRef}
//     steps={STEPS}
//     step={step}
//     titlePrefix="Bulk Actions"
//     onNext={onNext}
//     onGotIt={onGotIt}
//     onClose={onClose}
//     onStepChange={onStepChange}
//     style={{ left, top, transform, opacity, transition, transformOrigin }}
//   >
//     {/* arrow or other decorations */}
//   </GuidePanel>

import React from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * @param {Array<{title:string, body:string, nextLabel?:string}>} steps
 * @param {number}   step          Current page (1-indexed)
 * @param {string}   [titlePrefix] Bold prefix before the italic step title, e.g. "Bulk Actions"
 * @param {function} onNext        Advance to next page
 * @param {function} onGotIt       Hard dismiss (last page confirm)
 * @param {function} onClose       Soft dismiss
 * @param {function} onStepChange  Jump to any page (dot navigation)
 * @param {object}   style         Positioning + animation styles from parent
 * @param {React.ReactNode} children  Arrow or other decorations (absolutely positioned)
 */
const GuidePanel = React.forwardRef(function GuidePanel(
  { steps, step, titlePrefix, onNext, onGotIt, onClose, onStepChange, style, children },
  ref
) {
  const current = steps[(step - 1) % steps.length];

  return (
    <div
      ref={ref}
      className="fixed z-[140] bg-app-gray-800 text-app-white rounded-app-md w-[248px] p-app-md"
      style={style}
      aria-live="polite"
    >
      {/* Arrow or other decorations from parent */}
      {children}

      {/* Page dots — all clickable for free forward/backward navigation */}
      <div className="flex gap-1 mb-app-md">
        {steps.map((_, i) => (
          <button
            key={i}
            onClick={() => onStepChange?.(i + 1)}
            className={[
              'h-[3px] flex-1 rounded-full transition-colors duration-app-normal cursor-pointer',
              i < step ? 'bg-app-white' : 'bg-app-gray-700',
            ].join(' ')}
          />
        ))}
      </div>

      {/* Title */}
      <p className="text-app-xs text-app-white mb-app-xs">
        {titlePrefix ? (
          current.title ? (
            <>
              <span className="font-semibold">{titlePrefix}:</span>{' '}
              <em>{current.title}</em>
            </>
          ) : (
            <span className="font-semibold">{titlePrefix}</span>
          )
        ) : (
          <em>{current.title}</em>
        )}
      </p>

      {/* Body — min-h keeps the panel height stable across pages */}
      <p className="text-app-xs text-app-gray-400 mb-app-md min-h-[32px]">
        {current.body}
      </p>

      {/* Footer: Close left of Next/Got it, both right-aligned */}
      <div className="flex justify-end items-center gap-app-md">
        <button
          onClick={onClose}
          className="text-app-xs text-app-gray-400 hover:text-app-white transition-colors duration-app-normal mr-app-md"
        >
          Close
        </button>
        {step < steps.length ? (
          <button
            onClick={onNext}
            className="btn-text-link text-app-xs flex items-center gap-app-xs"
          >
            {current.nextLabel || 'Next'} <ChevronRight size={14} />
          </button>
        ) : (
          <button
            onClick={onGotIt}
            className="btn-text-link text-app-xs flex items-center gap-app-xs"
          >
            Got it <ChevronRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
});

export default GuidePanel;